import { RulesReadmodelType } from '@bett3r-dev/teselly-domain';
/**
 * Automation Shared State
 *
 * Contains always-on rule summary data for the account.
 * Powers the navbar AutomationDropdown indicator.
 */

export type AutomationSharedState = {
  /**
   * All rules for summary counts AND scope-suppression calculation.
   * Includes `kind: 'bypass'` rows (fetched explicitly in rulesQueryPolicy,
   * since the `/rules` route default-excludes them — TV1-1648). Bypass rows are
   * never rendered; `resolveRulesForScope` uses them to suppress targeted rules.
   */
  rules: RulesReadmodelType[];

  /** Whether the navbar dropdown is open */
  dropdownOpen: boolean;

  /** Loading states */
  loading: { rules: boolean };
};

export const initialState: AutomationSharedState = {
  rules: [],
  dropdownOpen: false,
  loading: { rules: false }
};

/**
 * Derived state selectors
 */
export const selectors = {
  /**
   * Total number of rules.
   */
  totalRulesCount: ( state: AutomationSharedState ): number =>
    state.rules.length,

  /**
   * Number of active (enabled) rules.
   * Rules with enabled !== false are considered active.
   */
  activeRulesCount: ( state: AutomationSharedState ): number =>
    state.rules.filter( r => r.enabled !== false ).length,

  /**
   * Rule count breakdown by category.
   * A single rule can belong to multiple categories.
   */
  rulesByCategory: ( state: AutomationSharedState ): Record<string, number> => {
    const counts: Record<string, number> = {};
    state.rules.forEach( r => {
      ( r.categories || []).forEach( cat => {
        counts[cat] = ( counts[cat] || 0 ) + 1;
      });
    });
    return counts;
  },

  /**
   * Rule count breakdown by entity type.
   * Rules with no entity are grouped under 'unknown'.
   */
  rulesByEntity: ( state: AutomationSharedState ): Record<string, number> => {
    const counts: Record<string, number> = {};
    state.rules.forEach( r => {
      const entity = r.hierarchyLevel || 'unknown';
      counts[entity] = ( counts[entity] || 0 ) + 1;
    });
    return counts;
  }
};
